import { useState, useRef, useEffect, useCallback } from 'react'

interface SearchSuggestion {
  id: string
  title: string
  category: string
  type: 'recent' | 'trending' | 'suggestion'
  icon?: string
}

type SearchMode = 'search' | 'ai'

interface SearchState {
  query: string
  isOpen: boolean
  searchMode: SearchMode
  suggestions: SearchSuggestion[]
  isLoading: boolean
}

interface SearchActions {
  setQuery: (query: string) => void
  setIsOpen: (isOpen: boolean) => void
  setSearchMode: (mode: SearchMode) => void
  setSuggestions: (suggestions: SearchSuggestion[]) => void
  setIsLoading: (isLoading: boolean) => void
  clearSearch: () => void
  performSearch: (query: string) => Promise<void>
  handleSearch: (query: string) => void
}

// Mock data
const recentSearches: SearchSuggestion[] = [
  { id: '1', title: 'React Hooks', category: 'tutorials', type: 'recent' },
  {
    id: '2',
    title: 'TypeScript Best Practices',
    category: 'articles',
    type: 'recent',
  },
  { id: '3', title: 'UI Komponenten', category: 'tools', type: 'recent' },
]

const trendingSearches: SearchSuggestion[] = [
  { id: '4', title: 'Next.js 14', category: 'tutorials', type: 'trending' },
  { id: '5', title: 'Tailwind CSS', category: 'tools', type: 'trending' },
  {
    id: '6',
    title: 'State Management',
    category: 'articles',
    type: 'trending',
  },
]

export const useSearch = (): SearchState &
  SearchActions & {
    searchRef: React.RefObject<HTMLDivElement | null>
    recentSearches: SearchSuggestion[]
    trendingSearches: SearchSuggestion[]
  } => {
  const [query, setQuery] = useState('')
  const [isOpen, setIsOpen] = useState(false)
  const [searchMode, setSearchMode] = useState<SearchMode>('search')
  const [suggestions, setSuggestions] = useState<SearchSuggestion[]>([])
  const [isLoading, setIsLoading] = useState(false)
  const searchRef = useRef<HTMLDivElement>(null)

  // Memoized setters
  const setQueryCallback = useCallback((newQuery: string) => {
    setQuery(newQuery)
  }, [])

  const setIsOpenCallback = useCallback((newIsOpen: boolean) => {
    setIsOpen(newIsOpen)
  }, [])

  const setSearchModeCallback = useCallback((newMode: SearchMode) => {
    setSearchMode(newMode)
  }, [])

  const setSuggestionsCallback = useCallback(
    (newSuggestions: SearchSuggestion[]) => {
      setSuggestions(newSuggestions)
    },
    []
  )

  const setIsLoadingCallback = useCallback((newIsLoading: boolean) => {
    setIsLoading(newIsLoading)
  }, [])

  // Search logic
  const performSearch = useCallback(
    async (searchQuery: string) => {
      setIsLoadingCallback(true)
      await new Promise(resolve => setTimeout(resolve, 300))

      const mockResults: SearchSuggestion[] = [
        {
          id: '7',
          title: `${searchQuery} Tutorial`,
          category: 'tutorials',
          type: 'suggestion',
        },
        {
          id: '8',
          title: `${searchQuery} Beispiele`,
          category: 'articles',
          type: 'suggestion',
        },
        {
          id: '9',
          title: `${searchQuery} Anleitung`,
          category: 'tutorials',
          type: 'suggestion',
        },
      ]

      setSuggestionsCallback(mockResults)
      setIsLoadingCallback(false)
    },
    [setIsLoadingCallback, setSuggestionsCallback]
  )

  // Effects
  useEffect(() => {
    if (query.length > 2) {
      performSearch(query)
    } else {
      setSuggestionsCallback([])
    }
  }, [query, performSearch, setSuggestionsCallback])

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        searchRef.current &&
        !searchRef.current.contains(event.target as Node)
      ) {
        setIsOpenCallback(false)
      }
    }

    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [setIsOpenCallback])

  const clearSearch = useCallback(() => {
    setQuery('')
    setSuggestionsCallback([])
    setIsOpenCallback(false)
  }, [setSuggestionsCallback, setIsOpenCallback])

  const handleSearch = useCallback(
    (searchQuery: string) => {
      setQuery(searchQuery)
      setIsOpenCallback(false)
    },
    [setIsOpenCallback]
  )

  return {
    // State
    query,
    isOpen,
    searchMode,
    suggestions,
    isLoading,
    searchRef,

    // Actions
    setQuery: setQueryCallback,
    setIsOpen: setIsOpenCallback,
    setSearchMode: setSearchModeCallback,
    setSuggestions: setSuggestionsCallback,
    setIsLoading: setIsLoadingCallback,
    clearSearch,
    performSearch,
    handleSearch,

    // Computed
    recentSearches,
    trendingSearches,
  }
}
